import { t as tr } from '../i18n.js'
import type { Task, TaskStatus } from '../api.js'
import type { SourceProject } from './taskSource.js'

/**
 * Board column model (pure). A column is one project's status. In project
 * mode the columns are that project's statuses in position order; in a
 * portfolio they are every project's statuses grouped by category and,
 * inside each category, ordered by project (portfolio order) then position:
 *   Backlog X, Backlog Y, Backlog Z, In progress X, In progress Y, … Done Z.
 * A task always belongs to the column whose id is its statusId.
 */
export type Category = TaskStatus['category']
export interface BoardColumn {
  id: string            // statusId
  projectId: string
  name: string
  category: Category
  color: string | null
  wipLimit: number | null
  position: number
}
export interface CategoryGroup { category: Category; label: string; span: number }

export const CATEGORIES: readonly Category[] = ['todo', 'in_progress', 'done', 'archived']

const toColumn = (project: SourceProject, s: TaskStatus): BoardColumn =>
  ({ id: s.id, projectId: project.id, name: s.name, category: s.category, color: s.color, wipLimit: s.wipLimit, position: s.position })

const byPosition = (a: TaskStatus, b: TaskStatus) => a.position - b.position

/** One project's columns, in status position order (the single-project board). */
export function projectColumns(project: SourceProject): BoardColumn[] {
  return [...project.statuses].sort(byPosition).map((s) => toColumn(project, s))
}

/** Every project's columns, grouped by category then by project (portfolio order), then by position. */
export function portfolioColumns(projects: SourceProject[]): BoardColumn[] {
  const out: BoardColumn[] = []
  for (const category of CATEGORIES) {
    for (const p of projects) {
      for (const s of [...p.statuses].sort(byPosition)) if (s.category === category) out.push(toColumn(p, s))
    }
  }
  return out
}

/** The column a task sits in. */
export const columnOf = (task: Pick<Task, 'statusId'>): string => task.statusId

export function categoryLabel(category: Category): string {
  return category === 'todo' ? tr("To do") : category === 'in_progress' ? tr("In progress") : category === 'done' ? tr("Done") : tr("Archived")
}

/** Contiguous runs of columns sharing a category, for the super-header row above the column headers. */
export function categoryGroups(columns: BoardColumn[]): CategoryGroup[] {
  const groups: CategoryGroup[] = []
  for (const c of columns) {
    const last = groups.at(-1)
    if (last && last.category === c.category) last.span++
    else groups.push({ category: c.category, label: categoryLabel(c.category), span: 1 })
  }
  return groups
}

/** A stack (one column, in one swimlane) shows at most this many cards; the rest are reached through another view. */
export const STACK_LIMIT = 25

/**
 * Cap a stack to the `limit` most recently moved cards (latest entry into
 * their column), keeping the board's own order among those shown. Ties go to
 * the card ranked higher on the board. `hidden` counts the cards left out.
 */
export function capStack<T extends Pick<Task, 'id' | 'statusChangedAt'>>(cards: T[], limit = STACK_LIMIT): { shown: T[]; hidden: number } {
  if (cards.length <= limit) return { shown: cards, hidden: 0 }
  const recent = new Set([...cards].map((c, i) => ({ c, i })).sort((a, b) => b.c.statusChangedAt.localeCompare(a.c.statusChangedAt) || a.i - b.i).slice(0, limit).map((x) => x.c.id))
  return { shown: cards.filter((c) => recent.has(c.id)), hidden: cards.length - limit }
}
